/**
 * The saved-prompt library's surface (2026-09-17).
 *
 * TWO SILHOUETTES, ONE PALETTE. The geometry comes from the thread sheet —
 * a bottom sheet on a phone, an anchored drop-up on a wider screen — because
 * that is this codebase's answer to "must work docked, floating and
 * fullscreen". The palette comes from the routine manager's light ground
 * (`#fbfaf6` on a warm scrim), because the split here is that navigation chrome
 * is dark and CONTENT YOU READ is light — and a prompt body is content, several
 * lines of prose somebody scans before committing to it.
 *
 * Gold appears only as a fill or a hairline, never as text on this light
 * ground: `scripts/check-gold-as-text.mjs` is the gate, and the deep
 * `#6b551f` used for headings is the sanctioned readable tone.
 */
import styled, { keyframes } from 'styled-components';
import { BODY_PORTAL_Z_INDEX } from '../../styles/layers';
import { mediaMax } from '../../styles/breakpoints';

const riseUp = keyframes`
  from { transform: translateY(12px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
`;

/**
 * The click-away layer. Body-portaled at BODY_PORTAL_Z_INDEX — this is a menu
 * belonging to the chat, and the chat pane's own backdrop-filter would trap a
 * fixed child at any z-index if it were not portaled out.
 *
 * Transparent on the anchored variant (a drop-up should not dim the thread
 * behind it) and dimmed under the phone sheet, which covers most of the screen.
 */
export const PromptsScrim = styled.div<{ $variant: 'sheet' | 'panel' }>`
  position: fixed;
  inset: 0;
  z-index: ${BODY_PORTAL_Z_INDEX};
  background: ${({ $variant }) => ($variant === 'sheet' ? 'rgba(15, 12, 6, 0.45)' : 'transparent')};
`;

export const PromptsSurface = styled.div<{ $variant: 'sheet' | 'panel'; $left?: number; $bottom?: number }>`
  position: fixed;
  z-index: ${BODY_PORTAL_Z_INDEX};
  display: flex;
  flex-direction: column;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #3a3327;
  background: #fbfaf6;
  border: 1px solid rgba(198, 167, 94, 0.35);
  box-shadow: 0 6px 24px rgba(60, 48, 20, 0.14);
  animation: ${riseUp} 0.16s ease-out;

  ${({ $variant, $left, $bottom }) => ($variant === 'panel'
    ? `
      left: ${Math.max(8, $left ?? 8)}px;
      bottom: ${Math.max(8, $bottom ?? 8)}px;
      width: min(420px, calc(100vw - 32px));
      max-height: min(56vh, 460px);
      border-radius: 14px;
    `
    : `
      left: 0;
      right: 0;
      bottom: 0;
      max-height: 72dvh;
      border-radius: 16px 16px 0 0;
      border-bottom: none;
      padding-bottom: env(safe-area-inset-bottom, 0px);
    `)}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** The phone sheet's grab handle — the thread sheet's own affordance. */
export const PromptsHandle = styled.div`
  width: 36px;
  height: 4px;
  margin: 8px auto 2px;
  border-radius: 999px;
  background: rgba(198, 167, 94, 0.45);
`;

export const PromptsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 6px;
`;

export const PromptsTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #6b551f;
`;

export const PromptsHeaderButton = styled.button`
  margin-left: auto;
  padding: 3px 9px;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  color: #6b551f;
  background: transparent;
  border: 1px solid rgba(182, 146, 76, 0.4);
  border-radius: 9px;
  cursor: pointer;
  min-height: 0;
  min-width: 0;

  &:hover { background: rgba(198, 167, 94, 0.1); }
  &:focus-visible { outline: 2px solid rgba(182, 146, 76, 0.6); outline-offset: 1px; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const PromptsSearchRow = styled.div`
  position: relative;
  padding: 0 14px 8px;
`;

export const PromptsSearchInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  font-family: inherit;
  font-size: 13px;
  color: #3a3327;
  background: #fff;
  border: 1px solid rgba(198, 167, 94, 0.32);
  border-radius: 8px;
  min-height: 0;

  &::placeholder { color: #9a8f7a; }
  &:focus { outline: 2px solid rgba(182, 146, 76, 0.6); outline-offset: -1px; border-color: transparent; }
  &::-webkit-search-cancel-button { display: none; }
`;

export const PromptsBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 10px;
  -webkit-overflow-scrolling: touch;
`;

export const PromptsGroupHeading = styled.div`
  padding: 8px 6px 4px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #8a7c5c;
`;

export const PromptRow = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  margin: 2px 0;
  font-family: inherit;
  background: ${({ $active }) => ($active ? 'rgba(198, 167, 94, 0.14)' : '#fff')};
  border: 1px solid rgba(198, 167, 94, 0.28);
  border-radius: 10px;
  cursor: pointer;
  min-height: 0;

  &:hover { background: rgba(198, 167, 94, 0.1); }
  &:focus-visible { outline: 2px solid rgba(182, 146, 76, 0.6); outline-offset: 1px; }
`;

export const PromptRowTitle = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: #3a3327;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PromptRowPreview = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  line-height: 1.45;
  color: #6f634a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${mediaMax.mobileViewport} { -webkit-line-clamp: 3; }
`;

export const PromptRowMeta = styled.div`
  margin-top: 3px;
  font-size: 10.5px;
  color: #8a7c5c;
`;

/** The quiet one-liner a body shows in place of rows. A fact, not an apology. */
export const PromptsNote = styled.div<{ $tone?: 'error' }>`
  padding: 10px 8px;
  font-size: 11.5px;
  line-height: 1.5;
  color: ${({ $tone }) => ($tone === 'error' ? '#8a5a2f' : '#6f634a')};
`;

export const PromptsFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px 12px;
  border-top: 1px solid rgba(198, 167, 94, 0.22);
`;

export const PromptsFooterButton = styled.button<{ $primary?: boolean }>`
  padding: 6px 12px;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  border-radius: 9px;
  cursor: pointer;
  min-height: 0;
  min-width: 0;
  color: ${({ $primary }) => ($primary ? 'var(--brand-charcoal)' : '#6b551f')};
  background: ${({ $primary }) => ($primary ? 'var(--brand-gold)' : 'transparent')};
  border: 1px solid ${({ $primary }) => ($primary ? 'var(--brand-gold)' : 'rgba(182, 146, 76, 0.4)')};

  &:hover { filter: brightness(0.97); }
  &:focus-visible { outline: 2px solid rgba(182, 146, 76, 0.6); outline-offset: 1px; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

// ── the editor and the fill-in step share these ─────────────────────────────

export const PromptFieldLabel = styled.label`
  display: block;
  padding: 8px 6px 3px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #8a7c5c;
`;

export const PromptTextInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  font-family: inherit;
  font-size: 13px;
  color: #3a3327;
  background: #fff;
  border: 1px solid rgba(198, 167, 94, 0.32);
  border-radius: 8px;
  min-height: 0;

  &::placeholder { color: #9a8f7a; }
  &:focus { outline: 2px solid rgba(182, 146, 76, 0.6); outline-offset: -1px; border-color: transparent; }
`;

export const PromptTextArea = styled.textarea`
  width: 100%;
  min-height: 132px;
  padding: 8px 10px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  color: #3a3327;
  background: #fff;
  border: 1px solid rgba(198, 167, 94, 0.32);
  border-radius: 8px;
  resize: vertical;

  &::placeholder { color: #9a8f7a; }
  &:focus { outline: 2px solid rgba(182, 146, 76, 0.6); outline-offset: -1px; border-color: transparent; }
`;

/** The live blank chips under the body field — how the {{ }} syntax teaches itself. */
export const PromptBlankChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 5px 6px 0;
`;

export const PromptBlankChip = styled.span`
  padding: 2px 8px;
  font-size: 10.5px;
  font-weight: 600;
  color: #6b551f;
  background: rgba(198, 167, 94, 0.14);
  border: 1px solid rgba(198, 167, 94, 0.32);
  border-radius: 999px;
`;

export const PromptEditorScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 4px;
`;
